import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import './presentation/styles/reset.css';
import './presentation/styles/tokens.css';
import './presentation/styles/utilities.css';

/**
 * Development Condition 2 (knowledge-tree §6): MSW is gated behind
 * `import.meta.env.DEV && VITE_ENABLE_MOCKS === 'true'`.
 * In a production build `DEV` is statically `false`, this branch is dead code and the
 * dynamic import is tree-shaken — MSW never ships in a production bundle.
 * A failed worker start surfaces a LOUD banner, never a silent white screen.
 */
async function boot(): Promise<void> {
  if (import.meta.env.VITE_ENABLE_MOCKS === 'true') {
    try {
      const [{ startMockWorker }, { apiClient }, { useAuthStore }] = await Promise.all([
        import('./infrastructure/mocks/browser'),
        import('./infrastructure/api/apiClient'),
        import('./application/stores/authStore'),
      ]);
      await startMockWorker();
      // Dev-only diagnostics handle used by scripts/runtime-proof.mjs to observe the
      // 401 → refresh → retry path in a real browser. DEV-gated: dead code in builds
      // (verified by grepping dist/ for __sgv after `npm run build`).
      (window as unknown as { __sgv?: unknown }).__sgv = { apiClient, authStore: useAuthStore };
    } catch (error) {
      console.error('[mocks] MSW worker failed to start', error);
      const { useMockBannerStore } = await import('./application/stores/mockBannerStore');
      useMockBannerStore
        .getState()
        .reportMockFailure(error instanceof Error ? error.message : String(error));
    }
  }

  const container = document.getElementById('root');
  if (!container) {
    throw new Error('Root container #root missing — cannot bootstrap application.');
  }

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void boot();
